import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import api from '../api';
import { useAuth } from '../context/auth';
import CompactPostCard from '../components/CompactPostCard';
import { usePromptResponseChooser } from '../components/PromptResponseChooser';
import { LoadingText } from '../components/Style';
import useInfiniteScroll from '../hooks/useInfiniteScroll';

var FeedContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;
  align-items: stretch;
  max-width: 410px;
  margin: 0 auto;
  padding: 1em 0.75em;
`;

var PromptHeader = styled.div`
  text-align: center;
  padding: 1.25rem 1.5rem;
  background: white;
  border: 1px solid #ddd;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 12px;
  box-sizing: border-box;
`;

var PromptText = styled.h1`
  font-weight: 700;
  font-size: 1.1rem;
  margin: 0 0 0.25rem 0;
  line-height: 1.4;
`;

var ResponseCount = styled.div`
  font-size: 0.8rem;
  color: #888;
  font-weight: 600;
`;

// Opens the post-or-list choice rather than going straight to a composer.
var RespondButton = styled.button`
  display: inline-block;
  background: black;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1.25rem;
  font-size: 0.8rem;
  font-weight: 700;
  font-family: inherit;
  text-decoration: none;
  margin-top: 0.75rem;
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }
`;

export default function PromptFeed() {
  var { promptId } = useParams();
  var respond = usePromptResponseChooser();

  var _prompt = useState(null);
  var prompt = _prompt[0];
  var setPrompt = _prompt[1];

  var _error = useState(null);
  var error = _error[0];
  var setError = _error[1];

  var authContext = useAuth();
  var authTokens = authContext.authTokens;
  var isLoggedIn = authTokens?.token;

  useEffect(
    function () {
      if (!promptId) return;
      api
        .get('/api/prompts/' + promptId)
        .then(function (response) {
          setPrompt(response.data);
          document.title = (response.data.name || response.data.text) + ' | HeavyRotation';
        })
        .catch(function () {
          setError(true);
        });
    },
    [promptId]
  );

  var fetchPage = useCallback(
    function (cursor) {
      var url = '/api/prompts/' + promptId + '/posts';
      var params = [];
      if (cursor) params.push('before=' + encodeURIComponent(cursor));
      params.push('limit=20');
      if (params.length) url += '?' + params.join('&');
      return api
        .get(url)
        .then(function (response) {
          return response.data;
        })
        .catch(function () {
          return { posts: [], hasMore: false, nextCursor: null };
        });
    },
    [promptId]
  );

  var scroll = useInfiniteScroll(fetchPage, [promptId]);
  var posts = scroll.posts;
  var loading = scroll.loading;
  var loadingMore = scroll.loadingMore;
  var sentinelRef = scroll.sentinelRef;
  function renderPost(post) {
    return <CompactPostCard key={post.repostId || post.id} post={post} />;
  }

  if (error) return <p style={{ textAlign: 'center', marginTop: '2rem' }}>Prompt not found.</p>;
  if (loading || !prompt) return <LoadingText>Loading...</LoadingText>;

  return (
    <FeedContainer>
      <PromptHeader>
        {prompt.name && <PromptText>{prompt.name}</PromptText>}
        <div
          style={{
            fontSize: '0.9rem',
            color: '#555',
            lineHeight: 1.4,
            marginBottom: prompt.name ? '0.5rem' : 0,
          }}
        >
          {prompt.text}
        </div>
        <ResponseCount>
          {prompt.responseCount} {prompt.responseCount === 1 ? 'response' : 'responses'}
        </ResponseCount>
        {isLoggedIn && (
          <RespondButton type="button" {...respond.triggerProps(promptId)}>
            Respond to this prompt
          </RespondButton>
        )}
      </PromptHeader>
      <div
        style={{
          fontWeight: 700,
          fontSize: '0.9rem',
          alignSelf: 'flex-start',
          padding: '0 0.25rem',
        }}
      >
        {posts.length > 0 ? 'Responses' : 'No responses yet. Be the first!'}
      </div>
      {posts.length > 0 && (
        <>
          {posts.map(renderPost)}
          <div ref={sentinelRef} />
          {loadingMore && <LoadingText>Loading more...</LoadingText>}
        </>
      )}
      {respond.chooser}
    </FeedContainer>
  );
}
